import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import ModuleApi from "api/ModuleApi";
import { normalizeLanguage } from "config/i18n";
import type { RawDocumentModule } from "helper/documentModules";
import { getApiData } from "../../types/api";

export interface ModulesQueryProps {
  modules: RawDocumentModule[];
  modulesLoaded: boolean;
}

const EMPTY_MODULES: RawDocumentModule[] = [];

export function modulesQueryKey(language: string | null) {
  return ["modules", language || "fr"];
}

export function useModules(language: string | null) {
  const normalizedLanguage = normalizeLanguage(language) || "fr";
  const query = useQuery({
    queryKey: modulesQueryKey(normalizedLanguage),
    queryFn: () =>
      ModuleApi.getAll(normalizedLanguage)
        .then((res) => getApiData(res, EMPTY_MODULES) as RawDocumentModule[])
        .catch(() => EMPTY_MODULES),
  });

  return {
    ...query,
    modules: query.data || EMPTY_MODULES,
    modulesLoaded: query.isSuccess,
  };
}

export function withModulesData<P extends ModulesQueryProps>(
  Component: React.ComponentType<P>
): React.ComponentType<Omit<P, keyof ModulesQueryProps>> {
  return function ModulesData(props) {
    const { i18n } = useTranslation();
    const modulesQuery = useModules(i18n.resolvedLanguage || null);

    return (
      <Component
        {...(props as P)}
        modules={modulesQuery.modules}
        modulesLoaded={modulesQuery.modulesLoaded}
      />
    );
  };
}
